import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  contacts: FunnelContact[];
  contactsLoading: boolean;
  selectedContact: string | null;

  handleSelectContact: (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleClose: () => void;

  handleTestPrompt: () => void;
}

const TestPromptDialog: React.FC<Props> = ({
  open,
  loading,
  contacts,
  contactsLoading,
  selectedContact,
  handleSelectContact,
  handleClose,
  handleTestPrompt,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('testPrompt')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <div>
            <Typography className={classes.formTitle} sx={{ mb: '8px' }}>
              {t('testContact')}
            </Typography>
            <Typography className={classes.formText}>{t('testContactDescription')}</Typography>
          </div>
          <PaiSelect value={selectedContact ?? ''} onChange={handleSelectContact} disabled={contactsLoading}>
            <PaiMenuItem value={'default'}>{t('defaultContact')}</PaiMenuItem>
            {contacts.map((contact) => (
              <PaiMenuItem key={contact.linkedInURL} value={contact.linkedInURL}>
                {contact.fullName
                  ? contact.fullName
                  : contact.firstName && contact.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact.linkedInURL}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('startTest')} size={'small'} border={'rounded'} action={handleTestPrompt} disabled={!selectedContact} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  formText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default TestPromptDialog;
